import React, { useState } from "react";
import axiosInstance from "../../config/axiosInstance";
import { CRM } from "../../config/apiConfig";
import {
  dateToHHMM,
  fileIconFor,
  fmtBR,
  labelIcon,
  prettySize,
} from "../../utils/utils";
import {
  HiOutlinePencil,
  HiCheckCircle,
  HiArrowPath,
  HiTrash,
} from "react-icons/hi2";
import moment from "moment";

function TaskItem({
  t,
  isEditing,
  isDone,
  labelMap,
  getUserNameById,
  editingData, // { editType, editNoteHtml, editDueDate, editDueTime, editResponsibleIds }
  setEditType,
  setEditNoteHtml,
  setEditDueDate,
  setEditDueTime,
  setEditResponsibleIds,
  userOptions,
  loadingUsers,
  startEditTask,
  saveEditTask,
  cancelEditTask,
  handleToggleTaskStatus,
  handleDeleteTask,
  onDeleteAttachment,
  onConfirmComplete,
}) {
  const [attLoading, setAttLoading] = useState({});

  // tarefa atrasada = tem dueDate, não está concluída e é anterior a agora
  const isOverdue =
    t?.dueDate &&
    t?.status !== "done" &&
    moment(t.dueDate).isBefore(moment(), "minute");
  const dueTime = (() => {
    if (!t?.dueDate) return "";
    const parsed = new Date(t.dueDate);
    if (Number.isNaN(parsed.getTime())) return "";
    return dateToHHMM(parsed);
  })();

  const responsibleNames = (() => {
    const list = Array.isArray(t?.responsibles)
      ? t.responsibles
          .map((r) => r?.name || getUserNameById(r?.id))
          .filter(Boolean)
      : [];
    if (list.length) return list;

    const fallback =
      t?.responsible?.name ||
      getUserNameById(t?.responsibleId) ||
      (t?.responsibleId ? String(t.responsibleId) : "");

    return fallback ? [fallback] : [];
  })();

  const completedByName =
    t?.completer?.name ||
    (t?.completedBy ? getUserNameById(t.completedBy) : null) ||
    null;

  async function openAttachment(att) {
    const key = att.id || att.objectName || att.gcsKey;
    setAttLoading((s) => ({ ...s, [key]: true }));
    try {
      const alreadyPublic =
        att.url || att.publicUrl || att.signedUrl || att.downloadUrl;
      if (alreadyPublic) {
        window.open(alreadyPublic, "_blank", "noopener,noreferrer");
        return;
      }
      const res = await axiosInstance.get(
        CRM.TASK_ATTACHMENT_SIGNED_URL(
          t.cardId || t.card_id || t.CardId,
          t.id,
          att.id
        ),
        { params: { ttl: 5 * 60 * 1000 } }
      );
      const url = res?.data?.data?.url;
      if (!url) throw new Error("URL não retornada");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error("openAttachment:", e);
      alert("Não foi possível abrir o anexo.");
    } finally {
      setAttLoading((s) => {
        const copy = { ...s };
        delete copy[key];
        return copy;
      });
    }
  }

  return (
    <div
      className={`rounded-md border p-2 transition-colors relative ${
        isDone ? "bg-green-50 border-green-200" : "bg-white border-gray-200"
      }`}
    >
      {isDone && (
        <span className="absolute bottom-2.5 right-2.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-100 border border-green-200 text-green-800">
          ✅ Concluída
        </span>
      )}

      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[12px] flex items-center gap-2">
            {labelIcon(t.type)}{" "}
            <span className="font-semibold">{labelMap[t.type] || "Ação"}</span>
          </div>

          {t.note && (
            <div
              className="rich-text text-[13px] mt-1 text-gray-800"
              dangerouslySetInnerHTML={{ __html: t.note || "" }}
            />
          )}

          {Array.isArray(t.attachments) && t.attachments.length > 0 && (
            <div className="mt-2 mb-2 text-[12px]">
              <div className="text-gray-600 font-medium mb-1">Anexos</div>
              <ul className="space-y-1">
                {t.attachments.map((a) => {
                  const name =
                    a.originalName ||
                    a.filename ||
                    a.name ||
                    a.objectName ||
                    a.gcsKey ||
                    "arquivo";
                  const size = a.size ? ` • ${prettySize(a.size)}` : "";
                  const icon = fileIconFor(
                    a.mimeType || a.mimetype || "",
                    name
                  );
                  const key = a.id || a.objectName || a.gcsKey || name;
                  const loading = attLoading[key];

                  return (
                    <li key={key} className="flex items-center gap-2">
                      <span className="shrink-0">{icon}</span>
                      <button
                        type="button"
                        className={`underline text-blue-600 hover:no-underline disabled:text-gray-400 disabled:no-underline ${
                          loading ? "cursor-wait" : ""
                        }`}
                        onClick={() => openAttachment(a)}
                        disabled={!!loading}
                        title={name}
                      >
                        {name}
                      </button>
                      <span className="text-gray-400">{size}</span>
                      {loading && (
                        <span className="text-gray-400"> • gerando link…</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="text-[11px] text-gray-600 mt-1 flex flex-wrap gap-2 md:items-center">
            {responsibleNames.length ? (
              <span className="flex items-center gap-1 flex-wrap">
                Atribuído para:
                {responsibleNames.map((name, idx) => (
                  <span
                    key={`${name}-${idx}`}
                    className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-gray-800"
                  >
                    {name}
                  </span>
                ))}
              </span>
            ) : (
              "Nenhum responsável"
            )}

            {/* Prazo em vermelho se estiver atrasada */}
            <span
              className={
                isOverdue ? "text-red-700 font-semibold" : "text-gray-600"
              }
            >
              Prazo:{" "}
              <b>
                {t?.dueDate
                  ? `${moment(t?.dueDate).format("DD/MM/yyyy")}`
                  : "Sem Prazo"}{" "}
              </b>
              {t?.dueDate && dueTime ? ` | ${dueTime}` : ""}
              {isOverdue && " • atrasada"}
            </span>

            <span>
              Criado por: <b>{t?.creator?.name}</b>
            </span>
            <span>
              Criado em: <b>{moment(t?.createdAt).format("DD/MM/yyyy")}</b>
            </span>
          </div>

          {isDone && t.completedAt && (
            <div className="mt-1 text-[11px] text-green-800">
              Concluída em{" "}
              <b>{moment(t.completedAt).format("DD/MM/yyyy HH:mm")}</b>
              {completedByName && (
                <>
                  {" "}por <b>{completedByName}</b>
                </>
              )}
            </div>
          )}
        </div>

        <div className="shrink-0 flex items-center gap-2">
          <button
            type="button"
            onClick={() => startEditTask(t)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:text-gray-800 transition"
            aria-label="Editar tarefa"
          >
            <HiOutlinePencil className="h-4 w-4" />
          </button>

          <button
            type="button"
            onClick={() => {
              if (t.status === "done") {
                handleToggleTaskStatus(t, false);
              } else {
                onConfirmComplete?.(t);
              }
            }}
            className={`inline-flex h-8 w-8 items-center justify-center rounded-md border bg-white text-xs transition ${
              t.status === "done"
                ? "border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700"
                : "border-emerald-400 text-emerald-600 hover:bg-emerald-50"
            }`}
            aria-label={
              t.status === "done" ? "Reabrir tarefa" : "Concluir tarefa"
            }
          >
            {t.status === "done" ? (
              <HiArrowPath className="h-4 w-4" />
            ) : (
              <HiCheckCircle className="h-4 w-4" />
            )}
          </button>

          <button
            type="button"
            onClick={() => handleDeleteTask(t)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-red-200 bg-white text-red-500 hover:border-red-300 hover:text-red-700 transition"
            aria-label="Excluir tarefa"
          >
            <HiTrash className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default React.memo(TaskItem);
